/**
 * Request/response schemas for the pediatrics specialty module (`BACKLOG.md` M8). Same reasoning as
 * `dermatologyRequests.ts`: these live in `shared/` so the server validates against the exact schema
 * the web app infers its types from, every object schema is strict so an unrecognised key is a
 * rejection rather than silently ignored, and no server-owned value (an id, `orgId`, a timestamp,
 * `recordedBy`) appears in any request schema — the route layer supplies those from the session,
 * never from the body.
 */

import { z } from 'zod';

import { calendarDateSchema, confidentialityReasonsInputSchema } from './clinicalRequests.ts';
import { CLINICAL_ERROR_CODES } from './encounterRequests.ts';
import { CLIENT_WRITABLE_GROWTH_MEASUREMENT_TYPES } from './pediatrics/growthCharts.ts';
import { SCREENING_RISK_LEVELS, SCREENING_TOOLS } from './pediatrics/developmentalScreening.ts';

// --- Growth measurements --------------------------------------------------------------------------

/**
 * Create only, matching `createVitalRequestSchema`'s own reasoning — a recorded measurement is
 * immutable, like an audit-log entry, not an editable chart note.
 *
 * `value` is a real `z.number()`, not text — unlike `vitals.value` (which stays open-but-checked
 * text because a vital reading is sometimes qualitative), a growth measurement is always a decimal
 * quantity in a fixed unit implied by `measurementType`, and `db/schema/pediatrics.ts`'s own
 * `pediatric_growth_measurements.value` column is a real `numeric` (mode `'number'`) for the
 * identical reason — the percentile engine needs a real number, not a string to re-parse.
 *
 * `measurementType` validates against `CLIENT_WRITABLE_GROWTH_MEASUREMENT_TYPES`, not the full
 * `GROWTH_MEASUREMENT_TYPES` — `'bmi'` is deliberately excluded here. A client posting
 * `measurementType: 'bmi'` is rejected outright rather than accepted and silently overwritten; BMI is
 * always server-computed from a same-encounter weight/height pair (see `growthCharts.ts`'s header).
 */
export const createGrowthMeasurementRequestSchema = z
  .object({
    measurementType: z.enum(CLIENT_WRITABLE_GROWTH_MEASUREMENT_TYPES),
    value: z.number().positive(),
    measuredOn: calendarDateSchema,
    encounterId: z.uuid().optional(),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateGrowthMeasurementRequest = z.infer<typeof createGrowthMeasurementRequestSchema>;

/**
 * The percentile/Z-score/source fields are computed at read time from `growthCharts.ts`'s
 * `computeGrowthPercentile` — never stored, so a later correction to the (currently UNREVIEWED)
 * reference table cannot leave a stale percentile behind on an old measurement row, matching
 * `destructionProcedureResponseSchema.cptLines`'s own reasoning. `percentileUnavailableReason` is
 * `null` exactly when `percentile`/`zScore` are present, and carries `computeGrowthPercentile`'s own
 * refusal reason otherwise (e.g. `'sex_not_supported'`) — a caller must not read a `null` percentile
 * as "0th percentile."
 */
export const growthMeasurementResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid().nullable(),
  measurementType: z.string(),
  value: z.number(),
  measuredOn: calendarDateSchema,
  confidentialityReasons: z.array(z.string()),
  percentile: z.number().nullable(),
  zScore: z.number().nullable(),
  growthChartSource: z.enum(['who', 'cdc']).nullable(),
  percentileUnavailableReason: z.string().nullable(),
});
export type GrowthMeasurementResponse = z.infer<typeof growthMeasurementResponseSchema>;

export const growthMeasurementEnvelopeSchema = z.object({
  measurement: growthMeasurementResponseSchema,
});
export const growthMeasurementListEnvelopeSchema = z.object({
  measurements: z.array(growthMeasurementResponseSchema),
});

// --- Developmental screening ------------------------------------------------------------------------

/**
 * `riskLevel` is required, not optional — a screening result with no classification at all is not a
 * useful chart entry, matching `updateDermatologyProfileRequestSchema`'s own "must carry a real
 * answer" reasoning. `score` is optional and, per `developmentalScreening.ts`'s own header, is never
 * used by this codebase to derive `riskLevel` — the two are independent fields end to end.
 */
export const createDevelopmentalScreeningRequestSchema = z
  .object({
    screeningTool: z.enum(SCREENING_TOOLS),
    screenedOn: calendarDateSchema,
    score: z.number().optional(),
    riskLevel: z.enum(SCREENING_RISK_LEVELS),
    encounterId: z.uuid().optional(),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateDevelopmentalScreeningRequest = z.infer<
  typeof createDevelopmentalScreeningRequestSchema
>;

export const developmentalScreeningResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid().nullable(),
  screeningTool: z.string(),
  screenedOn: calendarDateSchema,
  ageMonthsAtScreening: z.number().int().nonnegative(),
  score: z.number().nullable(),
  riskLevel: z.string(),
  confidentialityReasons: z.array(z.string()),
});
export type DevelopmentalScreeningResponse = z.infer<typeof developmentalScreeningResponseSchema>;

export const developmentalScreeningEnvelopeSchema = z.object({
  screening: developmentalScreeningResponseSchema,
});
export const developmentalScreeningListEnvelopeSchema = z.object({
  screenings: z.array(developmentalScreeningResponseSchema),
});

// --- Immunization gap list (reads the existing core `immunizations` table; writes none) -----------

export const missingVaccineResponseSchema = z.object({
  antigen: z.string(),
  label: z.string(),
  recommendedByAgeDays: z.number().int().nonnegative(),
});

export const immunizationGapEnvelopeSchema = z.object({
  patientAgeDays: z.number().int(),
  missing: z.array(missingVaccineResponseSchema),
});
export type ImmunizationGapEnvelope = z.infer<typeof immunizationGapEnvelopeSchema>;

// --- Bright Futures periodicity schedule (pure age-derived read, no persistence) -------------------

export const brightFuturesVisitResponseSchema = z.object({
  id: z.string(),
  label: z.string(),
});

export const brightFuturesEnvelopeSchema = z.object({
  patientAgeDays: z.number().int(),
  nearestVisit: brightFuturesVisitResponseSchema.nullable(),
  upcomingVisits: z.array(brightFuturesVisitResponseSchema),
});
export type BrightFuturesEnvelope = z.infer<typeof brightFuturesEnvelopeSchema>;

// --- Billing-code suggestions (pure reference lookup, no persistence) ------------------------------

export const billingSuggestionQuerySchema = z.object({
  newPatient: z.enum(['true', 'false']).default('false'),
  vaccineComponentCount: z.coerce.number().int().positive().optional(),
  withCounseling: z.enum(['true', 'false']).default('false'),
});
export type BillingSuggestionQuery = z.infer<typeof billingSuggestionQuerySchema>;

export const billingCodeLineResponseSchema = z.object({ cpt: z.string(), display: z.string() });

export const billingSuggestionEnvelopeSchema = z.object({
  preventiveEmCode: billingCodeLineResponseSchema.nullable(),
  vaccineAdministrationCodes: z.array(billingCodeLineResponseSchema).nullable(),
  developmentalScreeningCodes: z.array(billingCodeLineResponseSchema),
});
export type BillingSuggestionEnvelope = z.infer<typeof billingSuggestionEnvelopeSchema>;

// --- Failure vocabulary -----------------------------------------------------------------------------

export const PEDIATRICS_ERROR_CODES = [...CLINICAL_ERROR_CODES, 'not_entitled'] as const;
export type PediatricsErrorCode = (typeof PEDIATRICS_ERROR_CODES)[number];
